import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client with proper session handling
export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: localStorage,
      storageKey: 'fitscript-auth',
    },
  }
);

export const signUp = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/callback`,
    }
  });

  if (error) throw error;
  return data;
};

export const signIn = async (email: string, password: string) => {
  // Clear any existing session data before signing in
  localStorage.removeItem('fitscript-auth');
  
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
};

export const signOut = async () => {
  // Clear session data
  localStorage.removeItem('fitscript-auth');
  
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

const generateSecureState = () => {
  // Generate a cryptographically secure random string
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const connectOura = async () => {
  const clientId = import.meta.env.VITE_OURA_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_OURA_REDIRECT_URI;
  
  if (!clientId || !redirectUri) {
    throw new Error('Oura configuration not found');
  }

  // Define required Oura scopes
  const scopes = [
    'email',             // Basic user information
    'personal',          // Personal information
    'daily',             // Daily summaries
    'heartrate',         // Heart rate data
    'workout',           // Workout data
    'session',           // Session data
    'sleep'              // Sleep data
  ].join(' ');
  
  const state = generateSecureState();
  
  // Store state and current path in sessionStorage with expiration
  const stateData = {
    state,
    timestamp: Date.now(),
    returnPath: window.location.pathname
  };
  
  sessionStorage.setItem('ouraAuth', JSON.stringify(stateData));
  
  const authUrl = new URL('https://cloud.ouraring.com/oauth/authorize');
  authUrl.searchParams.append('client_id', clientId);
  authUrl.searchParams.append('redirect_uri', redirectUri);
  authUrl.searchParams.append('response_type', 'code');
  authUrl.searchParams.append('scope', scopes);
  authUrl.searchParams.append('state', state);

  console.log('[Oura Auth] Initiating OAuth flow');
  window.location.href = authUrl.toString();
};

export const connectStrava = async () => {
  const clientId = import.meta.env.VITE_STRAVA_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_STRAVA_REDIRECT_URI;
  
  if (!clientId || !redirectUri) {
    throw new Error('Strava configuration not found');
  }

  // Define required Strava scopes
  const scopes = [
    'read',              // Basic access
    'activity:read',     // Read activities
    'activity:read_all', // Read all activities
    'profile:read_all'   // Read extended profile information
  ].join(',');
  
  const state = generateSecureState();
  
  // Store state and current path in sessionStorage with expiration
  const stateData = {
    state,
    timestamp: Date.now(),
    returnPath: window.location.pathname
  };
  
  sessionStorage.setItem('stravaAuth', JSON.stringify(stateData));
  
  const authUrl = new URL('https://www.strava.com/oauth/authorize');
  authUrl.searchParams.append('client_id', clientId);
  authUrl.searchParams.append('redirect_uri', redirectUri);
  authUrl.searchParams.append('response_type', 'code');
  authUrl.searchParams.append('scope', scopes);
  authUrl.searchParams.append('state', state);
  authUrl.searchParams.append('approval_prompt', 'auto');

  console.log('[Strava Auth] Initiating OAuth flow');
  window.location.href = authUrl.toString();
};

const validateStoredState = (provider: 'oura' | 'strava'): { 
  isValid: boolean; 
  returnPath: string;
  state: string;
} => {
  const key = `${provider}Auth`;
  const storedData = sessionStorage.getItem(key);
  
  if (!storedData) {
    console.error(`[${provider} Auth] No stored state found`);
    return { isValid: false, returnPath: '/', state: '' };
  }
  
  try {
    const data = JSON.parse(storedData);
    const expirationTime = 5 * 60 * 1000; // 5 minutes
    
    // Check if state has expired
    if (Date.now() - data.timestamp > expirationTime) {
      console.error(`[${provider} Auth] State has expired`);
      sessionStorage.removeItem(key);
      return { isValid: false, returnPath: '/', state: '' };
    }
    
    return {
      isValid: true,
      returnPath: data.returnPath || '/',
      state: data.state
    };
  } catch (error) {
    console.error(`[${provider} Auth] Error parsing stored state:`, error);
    return { isValid: false, returnPath: '/', state: '' };
  } finally {
    // Clean up stored state
    sessionStorage.removeItem(key);
  }
};

export const handleOuraCallback = async (code: string, state: string) => {
  console.log('[Oura Auth] Processing callback');
  
  const storedState = validateStoredState('oura');
  if (!storedState.isValid || storedState.state !== state) {
    console.error('[Oura Auth] Invalid state:', { 
      receivedState: state,
      storedState: storedState.state,
      isValid: storedState.isValid 
    });
    throw new Error('Invalid or expired authentication state');
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('[Oura Auth] No authenticated session found');
      throw new Error('No authenticated session found');
    }

    console.log('[Oura Auth] Exchanging code for token');
    const response = await fetch('/.netlify/functions/oura-auth', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('[Oura Auth] Token exchange failed:', errorData);
      throw new Error(
        errorData?.message || 
        `Failed to exchange Oura code: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    if (!data.access_token) {
      console.error('[Oura Auth] Invalid response:', data);
      throw new Error('Invalid response from Oura');
    }
    
    console.log('[Oura Auth] Updating user record');
    const { data: updated, error: updateError } = await supabase
      .from('users')
      .update({
        oura_token: data.access_token,
        oura_refresh_token: data.refresh_token ?? null,
        oura_token_expiry: data.expires_in
          ? new Date(Date.now() + data.expires_in * 1000).toISOString()
          : null,
        updated_at: new Date().toISOString()
      })
      .eq('id', session.user.id)
      .select('id');

    if (updateError) {
      console.error('[Oura Auth] Failed to update user record:', updateError);
      throw updateError;
    }
    if (!updated || updated.length === 0) {
      throw new Error('Your profile is not set up yet, so the Oura connection could not be saved. Complete onboarding first, then reconnect Oura.');
    }

    console.log('[Oura Auth] Successfully completed OAuth flow');
    window.location.href = storedState.returnPath;

    return data;
  } catch (error) {
    console.error('[Oura Auth] Error:', error);
    throw error;
  }
};

export const handleStravaCallback = async (code: string, state: string) => {
  console.log('[Strava Auth] Processing callback');
  
  const storedState = validateStoredState('strava');
  if (!storedState.isValid || storedState.state !== state) {
    console.error('[Strava Auth] Invalid state:', { 
      receivedState: state,
      storedState: storedState.state,
      isValid: storedState.isValid 
    });
    throw new Error('Invalid or expired authentication state');
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('[Strava Auth] No authenticated session found');
      throw new Error('No authenticated session found');
    }

    console.log('[Strava Auth] Exchanging code for token');
    const response = await fetch('/.netlify/functions/strava-auth', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('[Strava Auth] Token exchange failed:', errorData);
      throw new Error(
        errorData?.message || 
        `Failed to exchange Strava code: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    if (!data.access_token || !data.refresh_token) {
      console.error('[Strava Auth] Invalid response:', data);
      throw new Error('Invalid response from Strava');
    }
    
    console.log('[Strava Auth] Updating user record');
    const { data: updated, error: updateError } = await supabase
      .from('users')
      .update({
        strava_token: data.access_token,
        strava_refresh_token: data.refresh_token,
        strava_token_expiry: new Date(Date.now() + data.expires_in * 1000).toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', session.user.id)
      .select('id');

    if (updateError) {
      console.error('[Strava Auth] Failed to update user record:', updateError);
      throw updateError;
    }
    if (!updated || updated.length === 0) {
      throw new Error('Your profile is not set up yet, so the Strava connection could not be saved. Complete onboarding first, then reconnect Strava.');
    }

    console.log('[Strava Auth] Successfully completed OAuth flow');
    window.location.href = storedState.returnPath;

    return data;
  } catch (error) {
    console.error('[Strava Auth] Error:', error);
    throw error;
  }
};

export const refreshStravaToken = async (userId: string, refreshToken: string) => {
  try {
    console.log('[Strava Auth] Refreshing token');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No authenticated session found');
    }

    const response = await fetch('/.netlify/functions/strava-auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('[Strava Auth] Token refresh failed:', errorData);
      throw new Error(
        errorData?.message || 
        `Failed to refresh Strava token: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    
    console.log('[Strava Auth] Updating user record with refreshed token');
    const { error } = await supabase
      .from('users')
      .update({
        strava_token: data.access_token,
        strava_refresh_token: data.refresh_token,
        strava_token_expiry: new Date(Date.now() + data.expires_in * 1000).toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

    if (error) {
      console.error('[Strava Auth] Failed to update user record:', error);
      throw error;
    }

    console.log('[Strava Auth] Successfully refreshed token');
    return data;
  } catch (error) {
    console.error('[Strava Auth] Error refreshing token:', error);
    throw error;
  }
};

/**
 * Both providers rotate the refresh token on every use, so two refreshes
 * launched from the same stale user row race: the first consumes the token
 * and the second gets an invalid_grant. The dashboard's stats hook and the
 * daily-recommendation load fire at the same time and each read the same
 * row, which is exactly that race — and the loser used to fall through to
 * placeholder metrics. Share one in-flight refresh per provider+user, then
 * remember the result so a later call with the (now stale) row doesn't
 * refresh again.
 */
interface RefreshResult { access_token: string; expires_in: number }

const inFlightRefreshes = new Map<string, Promise<RefreshResult>>();
const refreshedTokens = new Map<string, { accessToken: string; expiresAt: number }>();

const EXPIRY_MARGIN_MS = 5 * 60 * 1000;

/** A token from an earlier refresh this session, if it's still good. */
const cachedToken = (key: string): string | null => {
  const cached = refreshedTokens.get(key);
  return cached && cached.expiresAt - Date.now() > EXPIRY_MARGIN_MS
    ? cached.accessToken
    : null;
};

const refreshOnce = async (
  key: string,
  refresh: () => Promise<RefreshResult>
): Promise<RefreshResult> => {
  const pending = inFlightRefreshes.get(key);
  if (pending) return pending;

  const promise = refresh()
    .then((data) => {
      refreshedTokens.set(key, {
        accessToken: data.access_token,
        expiresAt: Date.now() + data.expires_in * 1000,
      });
      return data;
    })
    .finally(() => inFlightRefreshes.delete(key));

  inFlightRefreshes.set(key, promise);
  return promise;
};

/**
 * Returns a Strava access token that is safe to use right now,
 * refreshing (and persisting) it first if it expires within 5 minutes.
 * Falls back to the existing token if the refresh fails.
 */
export const getValidStravaToken = async (
  userId: string,
  token: string,
  refreshToken?: string,
  expiry?: string
): Promise<string> => {
  const expiresSoon = expiry && new Date(expiry).getTime() - Date.now() < EXPIRY_MARGIN_MS;
  if (!expiresSoon || !refreshToken) {
    return token;
  }

  const key = `strava:${userId}`;
  const cached = cachedToken(key);
  if (cached) return cached;

  try {
    const data = await refreshOnce(key, () => refreshStravaToken(userId, refreshToken));
    return data.access_token;
  } catch (error) {
    console.error('[Strava Auth] Using existing token after failed refresh:', error);
    return token;
  }
};

export const disconnectOura = async (userId: string) => {
  const { error } = await supabase
    .from('users')
    .update({
      oura_token: null,
      oura_refresh_token: null,
      oura_token_expiry: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId);
  if (error) throw error;
};

export const disconnectStrava = async (userId: string) => {
  const { error } = await supabase
    .from('users')
    .update({
      strava_token: null,
      strava_refresh_token: null,
      strava_token_expiry: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId);
  if (error) throw error;
};

export const refreshOuraToken = async (userId: string, refreshToken: string) => {
  console.log('[Oura Auth] Refreshing token');
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('No authenticated session found');
  }

  const response = await fetch('/.netlify/functions/oura-auth/refresh', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    console.error('[Oura Auth] Token refresh failed:', errorData);
    throw new Error(
      errorData?.error ||
      `Failed to refresh Oura token: ${response.status} ${response.statusText}`
    );
  }

  const data = await response.json();

  console.log('[Oura Auth] Updating user record with refreshed token');
  const { error } = await supabase
    .from('users')
    .update({
      oura_token: data.access_token,
      // Oura rotates the refresh token on every use
      oura_refresh_token: data.refresh_token,
      oura_token_expiry: new Date(Date.now() + data.expires_in * 1000).toISOString(),
      updated_at: new Date().toISOString()
    })
    .eq('id', userId);

  if (error) {
    console.error('[Oura Auth] Failed to update user record:', error);
    throw error;
  }

  return data;
};

/** Oura access tokens expire after 24h. Refresh when expired or expiring;
    tokens from before refresh support (no expiry stored) are treated as
    expired so a stale token is never silently used. */
export const getValidOuraToken = async (
  userId: string,
  token: string,
  refreshToken?: string,
  expiry?: string
): Promise<string | null> => {
  const needsRefresh = !expiry || new Date(expiry).getTime() - Date.now() < EXPIRY_MARGIN_MS;
  if (!needsRefresh) {
    return token;
  }

  const key = `oura:${userId}`;
  const cached = cachedToken(key);
  if (cached) return cached;

  if (!refreshToken) {
    // Pre-refresh-support connection: the token is a day old at most and
    // there is no way to renew it. Signal "reconnect needed" rather than
    // letting a dead token 401 downstream.
    console.warn('[Oura Auth] Token expired with no refresh token — reconnect Oura');
    return null;
  }

  try {
    const data = await refreshOnce(key, () => refreshOuraToken(userId, refreshToken));
    return data.access_token;
  } catch (error) {
    console.error('[Oura Auth] Token refresh failed:', error);
    return null;
  }
};